// app/layout.tsx

"use client";

import React, { useState, ReactNode, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as Global from "@utils/global";
import axios from "axios";
import "./layout.css";
import "./globals.css";
import { AuthProvider, useAuth } from "@/utils/authcontext";
import { AgentViewProvider } from "@/utils/agentviewcontext";
import AiAgent from "@/customs/aiagent";

/* =========================
   共通関数（外に出す）
   ========================= */
function displayDomainTitle(domaintitle: string) {
  if (!domaintitle) {
    return Global.systemTitle;
  }
  // const isProduction =
  //   typeof window !== "undefined" &&
  //   window.location.hostname === "oknetse.kmgr.jp";
  // return domaintitle + (isProduction ? "" : " DEVELOP");
  return domaintitle;
}

type Props = {
  children: ReactNode;
};

type MenuItem = {
  href: string;
  icon: string;
  label: string;
};

function getMenuItems(employeeGUID?: string | null): MenuItem[] {
  return [
    { href: "/", icon: "fas fa-home", label: "ホーム" },
    ...(employeeGUID
      ? [
          // { href: "/assistant", icon: "far fa-comment-dots", label: "アシスタント" },
          { href: "/signout", icon: "fa-solid fa-user-minus", label: "ログアウト" },
        ]
      : [
          { href: "/signon", icon: "fa-solid fa-user-plus", label: "ログイン" },
        ]),
    { href: "/privacy", icon: "fa-solid fa-circle-info", label: "ポリシー" },
  ];
}

function MenuLinks({
  items,
  onNavigate,
}: {
  items: MenuItem[];
  onNavigate?: () => void;
}) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item.href}>
          <Link href={item.href} onClick={onNavigate}>
            <i className={`${item.icon} menuicon-awesome`} />
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/* =========================
   内部レイアウト
   ========================= */
function LayoutContent({ children }: Props) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasAgent, setHasAgent] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const pathname = usePathname();
  const { domainTitle, employeeGUID, employeeDomain, employeeFlag, employeeName } = useAuth();

  const isEmployee = employeeFlag === "1";
  const isMenuHide = false;
  const menuItems = getMenuItems(employeeGUID);

  /* =========================
     title を動的に更新
     ========================= */
  useEffect(() => {
    const title = displayDomainTitle(domainTitle ?? "");
    document.title = title;
  }, [domainTitle]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1280px)");
    const updateViewportMode = () => setIsMobileViewport(mediaQuery.matches);
    updateViewportMode();
    mediaQuery.addEventListener("change", updateViewportMode);
    return () => {
      mediaQuery.removeEventListener("change", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    const loadAgentList = async () => {
      if (!employeeGUID || !employeeDomain) {
        setHasAgent(false);
        return;
      }
      try {
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/agenttools/myagentlist`, {
          base_interface: {
            user_guid: employeeGUID,
            user_domain: employeeDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          },
          body_data: {},
        });
        setHasAgent(Array.isArray(res.data?.agents) && res.data.agents.length > 0);
      } catch {
        setHasAgent(false);
      }
    };
    loadAgentList();
    window.addEventListener("agentlist-changed", loadAgentList);
    return () => {
      window.removeEventListener("agentlist-changed", loadAgentList);
    };
  }, [employeeGUID, employeeDomain]);

  return (
    <div className="layout">
      {!isMenuHide && (
        <>
          <button
            className="hamburger"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            ☰
          </button>

          {/* ===== サイドバー（PC） ===== */}
          <nav className="sidebar">
            <MenuLinks items={menuItems} />
            {!isMobileViewport && hasAgent && employeeGUID && employeeDomain && (
              <AiAgent
                employeeGUID={employeeGUID}
                employeeDomain={employeeDomain}
                idPrefix="aiagent-desktop"
              />
            )}
          </nav>

          {isMobileMenuOpen && (
            <div
              className="overlay"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* ===== サイドバー（モバイル） ===== */}
          {isMobileMenuOpen && (
            <div className="mobile-sidebar">
              <MenuLinks
                items={menuItems}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />
              {hasAgent && employeeGUID && employeeDomain && (
                <AiAgent
                  employeeGUID={employeeGUID}
                  employeeDomain={employeeDomain}
                  idPrefix="aiagent-mobile"
                />
              )}
            </div>
          )}
        </>
      )}

      {/* ===== メインコンテンツ ===== */}
      <div className="main-content">
        <div className="page-header">
          <Link href="/" className="page-header-link">
            <div
              style={{
                color: "black",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <img
                src="/oknetse32.png"
                width={24}
                height={24}
                alt="App Logo"
              />
              <span>
                {displayDomainTitle(domainTitle ?? "")}
                {employeeName ? ` (${employeeName})` : ""}
              </span>
            </div>
          </Link>
        </div>

        <div className="page-body">{children}</div>
      </div>
    </div>
  );
}

/* =========================
   RootLayout（最外）
   ========================= */
export default function RootLayout({ children }: Props) {
  return (
    <html lang="ja">
      <head>
        {/* 初期タイトル（後で LayoutContent が上書き） */}
        <title>{Global.systemTitle}</title>

        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      </head>
      <body>
        <AuthProvider>
          <AgentViewProvider>
            <LayoutContent>{children}</LayoutContent>
          </AgentViewProvider>
        </AuthProvider>
      </body>
    </html>
  );
}
